import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Home, PawPrint, BookOpen, UserRound, LogOut, Building2, ScrollText, Layers, MessageCircle, Bell, CalendarDays, GraduationCap, PenLine, ClipboardCheck, Users, LifeBuoy, KeyRound, ShoppingBag, FilePenLine, FileDown, BookMarked, Award, FileCheck, Files, Rocket, X, ChevronLeft, ChevronDown } from "lucide-react";
import DemoChrome from "./DemoChrome";
import { PublicHeader } from "./PublicHeader";
import { PublicFooter } from "./PublicFooter";
import { getDemo } from "../lib/demo";
import i18n from "../i18n";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useOnline } from "../lib/draft";
import { cx } from "./kit";
import { useMenuA11y } from "../lib/menu";

export function Wordmark({ to = "/" }) {
  return (
    <Link to={to} data-testid="wordmark" className="flex min-h-[44px] shrink-0 items-center gap-2.5" aria-label="Valeriia">
      <span className="relative inline-flex h-9 w-9 items-center justify-center rounded-xl border border-peach-line bg-peach">
        <span className="absolute -right-1 -top-1 h-3 w-3 rounded-full border-2 border-background bg-sage-mid" />
        <span className="font-display text-lg font-semibold leading-none text-clay-strong">V</span>
      </span>
      <span className="font-display text-[22px] font-semibold leading-none tracking-tight text-ink">Valeriia</span>
    </Link>
  );
}

export function LangSwitch() {
  const { me, setMe } = useAuth() || {};
  const cur = i18n.language?.startsWith("ru") ? "ru" : "en";
  const change = async (lng) => {
    if (lng === cur) return;
    localStorage.setItem("valeriia_lang", lng);
    localStorage.setItem("valeriia_lang_manual", "1");
    await i18n.changeLanguage(lng);
    if (me?.user) {
      try {
        const { data } = await api.put("/auth/profile", { interface_language: lng });
        setMe(data);
      } catch {}
    }
  };
  return (
    <div role="group" aria-label="Language / Язык" data-testid="language-switcher" className="inline-flex h-11 items-center text-sm font-semibold">
      {[["en", "English", "EN"], ["ru", "Русский", "RU"]].map(([l, label, code], i) => (
        <span key={l} className="flex items-center">
          {i === 1 && <span aria-hidden className="text-muted-foreground/60">·</span>}
          <button data-testid={`language-switcher-${l}`} lang={l} aria-pressed={cur === l} aria-current={cur === l ? "true" : undefined} aria-label={label} onClick={() => change(l)}
            className={cx("inline-flex h-11 min-w-[44px] items-center justify-center rounded-lg px-1.5 tracking-wide underline-offset-[6px] transition-colors", cur === l ? "text-ink underline decoration-clay decoration-2" : "text-muted-foreground hover:text-ink")}>
            <span aria-hidden>{code}</span><span className="sr-only">{label}</span>
          </button>
        </span>
      ))}
    </div>
  );
}

function ContextPill() {
  const { t } = useTranslation();
  const { me, setMe } = useAuth();
  const navigate = useNavigate();
  const u = me.user;
  const mems = me.memberships || [];
  const options = [...mems.map((m) => ({ v: `g:${m.workspace_id}`, label: `${m.workspace?.name} · ${t(`role.${m.role}`)}` }))];
  if (me.owner_dog_count > 0 || u.role_choice === "owner") options.push({ v: "owner", label: `${t("role.owner_space")} · ${t("role.owner")}` });
  const value = u.active_role === "owner" ? "owner" : `g:${u.active_workspace_id}`;
  const onChange = async (e) => {
    const v = e.target.value;
    const body = v === "owner" ? { role: "owner" } : { role: "groomer", workspace_id: v.slice(2) };
    const { data } = await api.post("/auth/active", body);
    setMe(data);
    navigate(v === "owner" ? "/owner" : "/home");
  };
  const active = options.find((o) => o.v === value);
  return (
    <label className="flex min-w-0 items-center gap-1.5 rounded-full border bg-sage px-3 py-1.5 text-sm font-semibold text-sage-ink" data-testid="workspace-role-pill">
      <Building2 className="h-4 w-4 shrink-0" aria-hidden />
      <span className="sr-only">{t("nav.active_context")}</span>
      {options.length > 1 ? (
        <select data-testid="workspace-role-select" value={value} onChange={onChange} className="min-w-0 max-w-[200px] sm:max-w-[280px] lg:max-w-[420px] truncate bg-transparent font-semibold focus:outline-none">
          {options.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
        </select>
      ) : <span className="truncate max-w-[200px] sm:max-w-[280px] lg:max-w-[420px]">{active?.label || (getDemo()?.role === "mentor" ? t("m.mentor_demo") : t("nav.no_workspace"))}</span>}
    </label>
  );
}

export function OfflineBar() {
  const { t } = useTranslation();
  const online = useOnline();
  if (online) return null;
  return <div data-testid="offline-bar" role="status" className="bg-peach px-4 py-2 text-center text-sm font-semibold text-clay-strong">{t("offline.bar")}</div>;
}

const SECTION = { dogs: "/practice", programmes: "/practice", handovers: "/practice", visits: "/calendar", tasks: "/home", cases: "/cases/manage", payment: "/profile" };
function parentOf(path, nav) {
  if (nav.some(([to]) => to === path)) return null;
  const pre = nav.filter(([to]) => path.startsWith(`${to}/`)).sort((a, b) => b[0].length - a[0].length)[0];
  return pre || nav.find(([to]) => to === SECTION[path.split("/")[1]]) || null;
}

export const learningFirst = (me) => me?.user?.active_role !== "owner" && !me?.user?.platform_admin && !(me?.user?.learning_roles || []).length;

export default function Layout({ children }) {
  const { t } = useTranslation();
  const { me, signOut } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [menu, setMenu] = useState(false);
  const toggleRef = useRef(null), panelRef = useRef(null);
  useMenuA11y(menu, () => setMenu(false), toggleRef, panelRef);
  useEffect(() => { setMenu(false); }, [loc.pathname]);
  const owner = me?.user?.active_role === "owner";
  const sandbox = !!me?.user?.sandbox_id;
  const admin = !!me?.user?.platform_admin;
  const lr = me?.user?.learning_roles || [];
  const ed = lr.some((r) => r === "editor" || r === "content_approver");
  const primary = owner
    ? [["/owner", Home, "nav.owner_home"], ["/messages", MessageCircle, "nav.messages"]]
    : [["/learning", GraduationCap, "nav.learning"], ["/practice", PawPrint, "nav.practice"], ["/library", BookOpen, "nav.library"], ["/calendar", CalendarDays, "nav.calendar"], ["/messages", MessageCircle, "nav.messages"]];
  const mine = owner
    ? [["/profile", UserRound, "nav.profile"]]
    : [["/home", Home, "nav.home"], ["/profile", UserRound, "nav.profile"], ["/workspace", Building2, "nav.workspace"], ["/mentoring", Users, "nav.mentoring"], ["/audit", ScrollText, "nav.audit"], ["/exports", FileDown, "nav.exports"], ["/cases/manage", BookMarked, "nav.cases"]];
  if (sandbox) mine.push(["/demo/modules", Layers, "sandbox.nav_modules"]);
  const staff = [];
  if (!owner) {
    if (lr.includes("editor")) staff.push(["/learning/editor", PenLine, "nav.editor"]);
    if (lr.includes("reviewer")) staff.push(["/learning/reviews", ClipboardCheck, "nav.reviews"], ["/learning/practical-queue", ClipboardCheck, "nav.practical_queue"]);
    if (lr.includes("mentor")) staff.push(["/mentoring/queue", Users, "nav.mentor_queue"]);
    if (ed) staff.push(["/admin/content", FilePenLine, "nav.content_admin"]);
  }
  if (admin || lr.includes("content_approver")) staff.push(["/admin/certificates", Award, "nav.certificates"]);
  if (admin) staff.push(["/admin/confirmations", FileCheck, "nav.confirmations"]);
  if (admin || ed) staff.push(["/admin/pages", Files, "nav.pages"]);
  if (admin) staff.push(["/admin/access", KeyRound, "nav.admin_access"], ["/admin/launch", Rocket, "nav.launch"]);
  const all = [...primary, ...mine, ...staff, ["/notifications", Bell, "nav.notifications"], ["/help", LifeBuoy, "nav.help"], ["/products", ShoppingBag, "nav.catalogue"]];
  const parent = parentOf(loc.pathname, all);
  const libActive = loc.pathname.startsWith("/library") || loc.pathname.startsWith("/products");
  const item = ([to, Icon, key]) => (
    <li key={to}><NavLink to={to} onClick={() => setMenu(false)} data-testid={`nav-item-${key.split(".")[1]}`} className={({ isActive }) => cx("flex min-h-[44px] items-center gap-3 rounded-xl px-3 text-sm font-medium transition-colors", isActive ? "bg-sand font-semibold text-ink" : "text-neutral-ink hover:bg-sand hover:text-ink")}>
      <Icon className="h-4 w-4 shrink-0 text-clay" aria-hidden />{t(key)}</NavLink></li>
  );
  const initial = (me?.user?.display_name || me?.user?.email || "?").trim()[0]?.toUpperCase();
  return (
    <div className="min-h-screen">
      <OfflineBar />
      {sandbox && <DemoChrome />}
      {menu && <div className="fixed inset-0 z-40 bg-ink/40 backdrop-blur-[2px] lg:bg-transparent lg:backdrop-blur-0" onClick={() => setMenu(false)} aria-hidden data-testid="app-menu-backdrop" />}
      <header className="sticky top-0 z-40 border-b border-sand-line bg-background lg:bg-background/95 lg:backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-6xl items-center gap-2 px-3 sm:gap-3 sm:px-6">
          <Wordmark to={owner ? "/owner" : "/home"} />
          <nav aria-label={t("nav.main")} data-testid="app-nav" className="fixed inset-x-0 bottom-0 z-40 border-t border-sand-line bg-background/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-md lg:static lg:ml-4 lg:border-0 lg:bg-transparent lg:pb-0">
            <ul className="mx-auto flex max-w-lg justify-around lg:max-w-none lg:justify-start lg:gap-1">
              {primary.map(([to, Icon, key]) => (
                <li key={to} className="min-w-0 flex-1 lg:flex-none">
                  <NavLink to={to} data-testid={`nav-item-${key.split(".")[1]}`} className={({ isActive }) => { const on = isActive || (to === "/library" && libActive); return cx("flex min-h-[56px] flex-col items-center justify-center gap-0.5 px-1 text-[11px] font-semibold transition-colors lg:min-h-[40px] lg:flex-row lg:gap-1.5 lg:rounded-full lg:px-3 lg:text-sm", on ? "text-ink lg:bg-sand" : "text-neutral-ink hover:text-ink lg:hover:bg-sand/70"); }}>
                    {({ isActive }) => <><Icon className={cx("h-5 w-5 shrink-0 lg:h-4 lg:w-4", isActive || (to === "/library" && libActive) ? "text-clay-strong" : "text-clay")} aria-hidden /><span className="max-w-full truncate">{t(key)}</span></>}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
          <div className="ml-auto flex items-center gap-1">
            <NavLink to="/notifications" data-testid="nav-item-notifications" aria-label={t("nav.notifications")} title={t("nav.notifications")} className={({ isActive }) => cx("inline-flex h-11 w-11 items-center justify-center rounded-full hover:bg-sand", isActive && "bg-sand")}><Bell className="h-5 w-5" aria-hidden /></NavLink>
            <NavLink to="/help" data-testid="nav-item-help" aria-label={t("nav.help")} title={t("nav.help")} className={({ isActive }) => cx("inline-flex h-11 w-11 items-center justify-center rounded-full hover:bg-sand", isActive && "bg-sand")}><LifeBuoy className="h-5 w-5" aria-hidden /></NavLink>
            <div className="relative">
              <button ref={toggleRef} type="button" data-testid="app-menu-toggle" aria-expanded={menu} aria-controls="account-menu" aria-haspopup="true" aria-label={t("b9.account")} onClick={() => setMenu(!menu)}
                className="inline-flex h-11 items-center gap-1.5 rounded-full border bg-card pl-1 pr-2.5 shadow-xs hover:bg-sand">
                <span className="grid h-9 w-9 place-items-center rounded-full bg-peach font-display font-semibold text-clay-strong" aria-hidden>{initial}</span><ChevronDown className="h-4 w-4" aria-hidden />
              </button>
              {menu && (
                <div id="account-menu" ref={panelRef} role="dialog" aria-label={t("b9.account")} data-testid="account-menu"
                  className="panel-in fixed inset-x-0 bottom-0 z-50 max-h-[85dvh] overflow-y-auto rounded-t-3xl border bg-card p-3 shadow-lift lg:absolute lg:inset-x-auto lg:bottom-auto lg:right-0 lg:top-full lg:mt-2 lg:max-h-[80vh] lg:w-80 lg:rounded-2xl">
                  <div className="flex items-start justify-between gap-2 border-b px-1 pb-3">
                    <div className="min-w-0"><p className="truncate font-semibold text-ink">{me?.user?.display_name}</p><p className="truncate text-xs text-muted-foreground">{me?.user?.email}</p></div>
                    <button type="button" data-testid="app-menu-close" aria-label={t("b7.close_menu")} onClick={() => { setMenu(false); toggleRef.current?.focus(); }} className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full hover:bg-sand"><X className="h-5 w-5" aria-hidden /></button>
                  </div>
                  <div className="py-3"><ContextPill /></div>
                  <ul className="space-y-0.5" aria-label={t("b9.mine")}>{mine.map(item)}</ul>
                  {staff.length > 0 && <><p className="eyebrow mt-3 px-3" id="admin-group">{t("b9.admin")}</p><ul className="mt-1 space-y-0.5" aria-labelledby="admin-group" data-testid="account-admin-group">{staff.map(item)}</ul></>}
                  <div className="mt-3 flex items-center justify-between border-t px-1 pt-3"><LangSwitch />
                    <button data-testid="sign-out-btn" onClick={() => { signOut(); navigate("/login"); }} className="inline-flex min-h-[44px] items-center gap-2 rounded-xl px-3 text-sm font-semibold hover:bg-sand"><LogOut className="h-4 w-4" aria-hidden />{t("nav.sign_out")}</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 pb-28 pt-6 sm:px-6 sm:pt-10 lg:pb-10">
        {parent && <Link to={parent[0]} data-testid="layout-back-link" className="-mt-2 mb-3 inline-flex min-h-control items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-foreground sm:-mt-4"><ChevronLeft className="h-4 w-4" aria-hidden />{t("b5.back_to", { section: t(parent[2]) })}</Link>}
        {libActive && !owner && <LibraryTabs />}
        {children}
      </main>
    </div>
  );
}

function LibraryTabs() {
  const { t } = useTranslation();
  const tab = (to, key, testid) => <NavLink to={to} data-testid={testid} className={({ isActive }) => cx("inline-flex min-h-[40px] items-center rounded-full px-4 text-sm font-semibold transition-colors", isActive ? "bg-ink text-white" : "text-neutral-ink hover:bg-sand")}>{t(key)}</NavLink>;
  return (
    <div className="mb-5 flex flex-wrap items-center gap-3">
      <img src="/img/b9/cover_a.webp" alt="" aria-hidden loading="lazy" className="hidden h-12 w-20 rounded-xl object-cover sm:block" />
      <div role="navigation" aria-label={t("nav.library")} className="inline-flex gap-1 rounded-full border bg-card p-1" data-testid="library-tabs">
        {tab("/library", "b5.nav.methodology", "library-tab-methodology")}{tab("/products", "nav.catalogue", "nav-item-catalogue")}
      </div>
    </div>
  );
}

export function BareLayout({ children }) {
  return (
    <div className="min-h-screen grain">
      <OfflineBar />
      <PublicHeader />
      <main className="mx-auto max-w-6xl px-4 pb-12 sm:px-6">{children}</main>
      <PublicFooter />
    </div>
  );
}
